/* Core & libraries */
import React, { useState, useEffect } from "react";
import { useTheme } from "react-jss";
import {defineMessages, FormattedMessage, useIntl} from "react-intl";
import { useNavigate } from "react-router";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { useLocation } from "react-router-dom"

/* Helpers, constants, custom methods, internal libraries */
import { REPORTS_ROUTE, REPORTS_HOTEL_BOOKING_ROUTE, REPORTS_SPEND_ROUTE, REPORTS_COMPLIANCE_ROUTE } from "../../../constants/routes";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useHasPermission } from "../../../hooks/useHasPermission";

/* Components */
import HotelBookingsTab from "./hotelBookingsTab";
import SpendReportTab from "./spendReportTab";
import ComplianceTab from "./complianceTab";
import UCSelect from "../../ucselect";
import UCButton from '../../buttons';
import Popover from 'react-bootstrap/Popover';
import { ReactComponent as FolderQuestionIconSvg } from "../../../resources/images/folder-question.svg";

/* Styles */
import styles, { tabsSelectOpen } from "./styles.js"
import { OverlayTrigger } from 'react-bootstrap';
import generalStyles from '../../../resources/styles/general';


const ReportsTabbedNavCHOICE = () => {

  const location = useLocation();

  const theme = useTheme();
  const classes = styles({ theme });
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { checkPermission } = useHasPermission();
  const [showRetroModal, setShowRetroModal] = useState(false);
  const general = generalStyles({ theme });
  const [defaultPage, setDefaultPage] = useState("hotel-bookings");


  useEffect(() => {
    setDefaultPage(getDefaultPage())
  }, [location.pathname]);


  const tabOptions = [
    ...(checkPermission("F_VIEW_HOTEL_BOOKINGS_REPORT") ?
      [{value: 'hotel-bookings', label: <FormattedMessage id="reports.hotel.booking.title" /> }] : []),
    ...(checkPermission("F_VIEW_SPEND_REPORT") ?
      [{value: 'spend', label: <FormattedMessage id="reports.hotel.spend.title" /> }] : []),
    ...(checkPermission("F_VIEW_POLICY_COMPLIANCE_REPORT") ?
      [{value: 'policy-compliance', label: <FormattedMessage id="reports.hotel.compliance.title" /> }] : []),
  ]

  const getDefaultPage = () => {
    if(location.pathname.includes(REPORTS_HOTEL_BOOKING_ROUTE)) return "hotel-bookings"
    if(location.pathname.includes(REPORTS_SPEND_ROUTE)) return "spend"
    if(location.pathname.includes(REPORTS_COMPLIANCE_ROUTE)) return "policy-compliance"
    if(location.pathname.includes(REPORTS_ROUTE)) return "hotel-bookings"
    return "hotel-bookings";
  }

  const getDefaultLabel = () => {
    if(location.pathname.includes(REPORTS_HOTEL_BOOKING_ROUTE)) return <FormattedMessage id="reports.hotel.booking.title"/>
    if(location.pathname.includes(REPORTS_SPEND_ROUTE)) return <FormattedMessage id="reports.hotel.spend.title"/>
    if(location.pathname.includes(REPORTS_COMPLIANCE_ROUTE)) return <FormattedMessage id="reports.hotel.compliance.title"/>
    if(location.pathname.includes(REPORTS_ROUTE)) return <FormattedMessage id="reports.hotel.booking.title" />
  }

  const handleTabChange = nextTab => {
    switch(nextTab) {
      case "hotel-bookings":
        navigate('../' + REPORTS_HOTEL_BOOKING_ROUTE);
        break;
      case "spend":
        navigate('../' + REPORTS_SPEND_ROUTE)
        break;
      case "policy-compliance":
        navigate('../' + REPORTS_COMPLIANCE_ROUTE)
        break;
      default:
        navigate('../' + REPORTS_HOTEL_BOOKING_ROUTE);
    }
  }

  const viewTab = tab => {
    switch(tab) {
      case "hotel-bookings":
        return <HotelBookingsTab />
      case "spend":
        return <SpendReportTab />
      case "policy-compliance":
        return <ComplianceTab />
    }
  }

  const renderInfoMissingData = () => {
    return(
      <Popover className={classes.infoPopover}>
        <div className={classes.infoPopoverDiv}>
          <div className={classes.infoTitle}>
            <FormattedMessage id="choice.reports.tooltip.title" />
          </div>
          <div className={classes.infoDescription}>
            <FormattedMessage id="choice.reports.tooltip.description" />
          </div>
        </div>
      </Popover>
    )
  }

  return (
    <div className={classes.reportsTabbedNav}>
      {isMobile ? (
        <>
          <UCSelect
            styles={{ ...tabsSelectOpen }}
            defaultValue={{
              value: getDefaultPage() || defaultPage,
              label: getDefaultLabel()
            }}
            options={tabOptions}
            onChange={(tab) => handleTabChange(tab.value)}
          />
          {
            checkPermission("F_RETRO_REWARDS_VIEW") &&
            <UCButton
              onClick={() => setShowRetroModal(true)}
              type="default"
              text={<FormattedMessage id="reports.missingMiles"/>}
              className={classes.missingAwardButton}
            />
          }
          {
            <div className={classes.missingAwardButton} >
              <OverlayTrigger
                placement="bottom"
                //show={true}
                overlay={renderInfoMissingData()}
              >
                <div className={classes.reportInfo}>
                  <FolderQuestionIconSvg className={classes.iconStyle} />
                  <p> <FormattedMessage id="choice.reports.tooltip.text" /> </p>
                </div>
              </OverlayTrigger>
            </div>
          }
          {viewTab(defaultPage || getDefaultPage())}
        </>
      ) : (
        <div className="position-relative">
          <Tabs
            onSelect={nextTab => handleTabChange(nextTab)}
            defaultActiveKey={ getDefaultPage() }
            activeKey={defaultPage}
            id="reports-page-tabs"
            className={ classes.tabs }
          >
            {
              checkPermission("F_VIEW_HOTEL_BOOKINGS_REPORT") &&
              <Tab eventKey="hotel-bookings" title={<FormattedMessage id="reports.hotel.booking.title" />}>
                { getDefaultPage() === "hotel-bookings" && <HotelBookingsTab />}
              </Tab>
            }
            {
              checkPermission("F_VIEW_SPEND_REPORT") &&
              <Tab eventKey="spend" title={<FormattedMessage id="reports.hotel.spend.title" />}>
                { getDefaultPage() === "spend" && <SpendReportTab />}
              </Tab>
            }
            {
              checkPermission("F_VIEW_POLICY_COMPLIANCE_REPORT") &&
              <Tab eventKey="policy-compliance" title={<FormattedMessage id="reports.hotel.compliance.title" />}>
                { getDefaultPage() === "policy-compliance" && <ComplianceTab /> }
              </Tab>
            }
          </Tabs>
            <div className={classes.missingAwardButton} >
              <OverlayTrigger
                placement="left"
                //show={true}
                overlay={renderInfoMissingData()}
              >
                <div className={classes.reportInfo}>
                  <FolderQuestionIconSvg className={classes.iconStyle} />
                  <p> <FormattedMessage id="choice.reports.tooltip.text" /> </p>
                </div>
              </OverlayTrigger>
            </div>
        </div>
      )}
    </div>
  )
}

export default ReportsTabbedNavCHOICE;
